/**
 * AI-Ops + platform data layer for the admin dashboard.
 *
 * Talks to the live backend via the Vite dev proxy (or absolute URLs in prod):
 *   /agent     -> AI Ops agent  (:8801)  chat / automate / sweep / system-health
 *   /api       -> API gateway   (:5050)  /api/* microservice CRUD
 *
 * Tenancy: every gateway call carries the `x-tenant-id` header.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const AGENT = (import.meta.env.VITE_AGENT_URL as string) || '/agent'
// gatewayGet() below always calls paths starting with "/api/...", so strip a trailing "/api" from
// VITE_API_URL here too (same fix as lib/gateway.ts) — otherwise every KPI count 404s on
// "/api/api/..." and silently degrades to "—".
const RAW_API = (import.meta.env.VITE_API_URL as string) || '/api'
const API = RAW_API.replace(/\/api\/?$/, '')
const TENANT = (import.meta.env.VITE_TENANT_ID as string) || 'default'

// ---------- types ---------------------------------------------------------- //
export type HealthState = 'healthy' | 'reachable' | 'down'

export interface ServiceHealth {
  service: string
  address: string
  status_code: number | null
  state: HealthState
}

export interface SystemHealth {
  total: number
  healthy: number
  unhealthy: ServiceHealth[]
  services: ServiceHealth[]
  error?: string
}

export interface AgentReply {
  ok: boolean
  answer?: string
  error?: string
  tool_invocations?: number
  persona?: string
}

export interface AgentInfo {
  status: string
  persona?: string
  model?: string
  tools_loaded?: number
  mcp_servers?: Record<string, string>
  writes_enabled?: boolean
}

// ---------- low-level fetch ------------------------------------------------ //
async function getJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', 'x-tenant-id': TENANT, ...(init?.headers || {}) },
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  return res.json() as Promise<T>
}

// ---------- agent API ------------------------------------------------------ //
export const opsApi = {
  agentInfo: () => getJSON<AgentInfo>(`${AGENT}/health`),
  systemHealth: () => getJSON<SystemHealth>(`${AGENT}/system-health`),
  sweep: () => getJSON<AgentReply>(`${AGENT}/sweep`, { method: 'POST' }),
  chat: (message: string) =>
    getJSON<AgentReply>(`${AGENT}/chat`, { method: 'POST', body: JSON.stringify({ message }) }),
  automate: (goal: string) =>
    getJSON<AgentReply>(`${AGENT}/automate`, { method: 'POST', body: JSON.stringify({ goal }) }),
  // generic read through the gateway (e.g. '/api/orders', '/api/tenants')
  gatewayGet: <T = unknown>(path: string) => getJSON<T>(`${API}${path}`),
}

// ---------- React Query hooks --------------------------------------------- //
export function useAgentInfo() {
  return useQuery({ queryKey: ['agent-info'], queryFn: opsApi.agentInfo, staleTime: 15_000 })
}

export function useSystemHealth(refetchMs = 30_000) {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: opsApi.systemHealth,
    refetchInterval: refetchMs,
    staleTime: refetchMs / 2,
  })
}

export function useSweep() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: opsApi.sweep,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['system-health'] }),
  })
}

export function useAgentChat() {
  return useMutation({ mutationFn: (message: string) => opsApi.chat(message) })
}

export function useAgentAutomate() {
  return useMutation({ mutationFn: (goal: string) => opsApi.automate(goal) })
}

/** KPI counts pulled from the gateway (best-effort; missing endpoints degrade gracefully). */
export function useKpis() {
  return useQuery({
    queryKey: ['kpis', TENANT],
    refetchInterval: 10_000,
    queryFn: async () => {
      const count = async (path: string): Promise<number | null> => {
        try {
          const data = await opsApi.gatewayGet<any>(path)
          if (Array.isArray(data)) return data.length
          if (typeof data?.count === 'number') return data.count
          if (Array.isArray(data?.items)) return data.items.length
          return null
        } catch {
          return null
        }
      }
      const [tenants, orders, devices, tickets] = await Promise.all([
        count('/api/tenants'),
        count('/api/orders'),
        count('/api/devices'),
        count('/api/tickets'),
      ])
      return { tenants, orders, devices, tickets }
    },
  })
}
